import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { hiddenWorksApi, type Site } from '@/api/endpoints';
import { Alert, EmptyState, FilterBar, Spinner } from '@/components/ui';
import { HiddenWorkDetail, HiddenWorkList } from '@/features/hidden-works/HiddenWorkViews';
import { PendingCard, usePendingItems } from '@/features/upload-queue';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { siteDayRange } from '@/lib/site-time';

/** 服务端 `take: 200`，和「本工地」一样。取满了要说出来 */
const MY_LIST_CAP = 200;

/**
 * 我在这个工地拍过的隐蔽工程（CR-031 加的「最近记录」，CR-042 收进隐蔽工程页的「我的」tab，CR-082 重做）。
 *
 * 拍完提交就什么也看不到是不行的 —— 传上去没有、上次那处拍过没有，全无从确认。
 * 走 `me/hidden-works?siteId=`：拍的人永远能看自己拍的，不需要「查看全工地」（CR-040）。
 * 和「本工地」同一种列表和详情（`HiddenWorkViews`），还没传上去的摆在最上面。
 *
 * **区间和类别由页面给**（CR-048）：服务端按同样的条件筛。拍摄人那一项在这里没有意义（永远是自己），不出现。
 */
export function MyHiddenWorksList({
  site,
  from,
  to,
  category,
  toolbar,
}: {
  site: Site;
  from: string;
  to: string;
  category: string;
  /** 工具条左边：本工地 / 我的、日期、类别 */
  toolbar?: ReactNode;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const [openId, setOpenId] = useState<string | null>(null);

  const recent = useQuery({
    queryKey: ['my-hidden-works', site.id, from, to, category],
    queryFn: () =>
      hiddenWorksApi.mine(site.id, {
        category: category || undefined,
        // 同「本工地」：区间按工地时区换算，不能在日期后面直接拼 Z
        ...siteDayRange(from, to, site.timezone),
      }),
  });

  // 还在队列里的隐蔽工程（CR-037）：服务端还没有它们，摆在最上面标「上传中」
  const pending = usePendingItems('HIDDEN_WORK', site.id);
  const list = recent.data ?? [];
  const opened = list.find((r) => r.id === openId) ?? null;

  return (
    <>
      <FilterBar
        summary={
          list.length > 0 && (
            <span className="flex flex-wrap gap-x-4">
              <span>{t('hidden.totalRecords', { count: list.length })}</span>
              {list.length >= MY_LIST_CAP && (
                <span className="text-safety-600">{t('hidden.cappedHint', { count: MY_LIST_CAP })}</span>
              )}
            </span>
          )
        }
      >
        {toolbar}
      </FilterBar>

      {recent.isLoading && (
        <div className="flex justify-center py-8 text-ink-3">
          <Spinner />
        </div>
      )}
      {recent.isError && <Alert>{toMessage(recent.error)}</Alert>}

      {pending.length > 0 && (
        <ul className="mb-3 flex flex-col gap-2">
          {pending.map((item) => (
            <PendingCard
              key={item.id}
              item={item}
              title={String(item.payload.locationDesc ?? t('queue.kindHiddenWork'))}
              subtitle={String(item.payload.category ?? '')}
            />
          ))}
        </ul>
      )}

      {!recent.isLoading && list.length === 0 && pending.length === 0 && <EmptyState title={t('hidden.empty')} />}

      <HiddenWorkList records={list} timezone={site.timezone} onOpen={setOpenId} />
      <HiddenWorkDetail record={opened} timezone={site.timezone} onClose={() => setOpenId(null)} />
    </>
  );
}
